/**
 * CountryCombobox — searchable typeahead country selector.
 *
 * A drop-in replacement for `<select>` when the list of options is
 * long (~200 countries). Uses cmdk under the hood for fast fuzzy
 * search across Italian names, English names (via Intl.DisplayNames),
 * and ISO codes.
 *
 * Fabrizio request 24/07/2026: dropdown "Paese di nascita" con solo
 * 35 paesi non copriva l'utenza reale (Bohdan Slutinsky → BY). Al
 * posto di espandere la lista static a 193 opzioni non ricercabili,
 * questo componente porta un'esperienza da CF/passaporto scanner:
 * l'utente digita "biel" e trova immediatamente 🇧🇾 Bielorussia.
 *
 * Design notes:
 *   • Renders a pill-shaped trigger button that looks like the existing
 *     <select> in the wallet flow — same border, background, hover.
 *   • Popover width = trigger width (min 260px).
 *   • Search matches: ISO code, Italian native name, English name,
 *     localized name in current UI language.
 *   • Keyboard: type to filter, ↑/↓ to navigate, Enter to select,
 *     Esc to close.
 *   • Loading = none (all data is static and bundled).
 */
import { useState, useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Check, ChevronDown, Search as SearchIcon, X } from "lucide-react";
import { ALL_ISO_COUNTRIES, findCountryByIso } from "../../constants/allCountries";
import { getLocalizedCountryName } from "../../constants/countries";

const T = {
  IT: { placeholder: "Seleziona un paese...", searchPlaceholder: "Cerca (es. Bielorussia, BY, biel...)", empty: "Nessun paese trovato" },
  UK: { placeholder: "Select a country...", searchPlaceholder: "Search (e.g. Belarus, BY, bel...)", empty: "No country found" },
  FR: { placeholder: "Sélectionnez un pays...", searchPlaceholder: "Rechercher...", empty: "Aucun pays trouvé" },
  ES: { placeholder: "Seleccione un país...", searchPlaceholder: "Buscar...", empty: "Ningún país encontrado" },
  DE: { placeholder: "Land auswählen...", searchPlaceholder: "Suchen...", empty: "Kein Land gefunden" },
  NL: { placeholder: "Selecteer een land...", searchPlaceholder: "Zoeken...", empty: "Geen land gevonden" },
  PT: { placeholder: "Selecione um país...", searchPlaceholder: "Pesquisar...", empty: "Nenhum país encontrado" },
  PL: { placeholder: "Wybierz kraj...", searchPlaceholder: "Szukaj...", empty: "Nie znaleziono kraju" },
};
const getT = (lang) => T[(lang || "IT").toUpperCase()] || T.IT;

// Simple normalization for fuzzy matching (strip accents + lowercase)
const norm = (s) => (s || "")
  .toString()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase();

export const CountryCombobox = ({
  value,
  onChange,
  lang = "IT",
  excludeCode = null,     // e.g. "IT" to hide Italy from the birth country picker
  placeholder = null,
  required = false,
  disabled = false,
  testId = "country-combobox",
  className = "",
}) => {
  const t = getT(lang);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Focus the search input the moment the popover opens.
  useEffect(() => {
    if (open) {
      const to = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(to);
    }
  }, [open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Pre-compute the pool once per language (localized names).
  const pool = useMemo(() => {
    return ALL_ISO_COUNTRIES
      .filter((c) => c.code !== excludeCode)
      .map((c) => {
        const localized = getLocalizedCountryName(c.code, lang) || c.name_it;
        return {
          ...c,
          localized,
          // Precompute the searchable haystack (all names + ISO code)
          _hay: `${norm(localized)} ${norm(c.name_it)} ${norm(c.code)}`,
        };
      })
      .sort((a, b) => a.localized.localeCompare(b.localized));
  }, [excludeCode, lang]);

  const results = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return pool;
    // Split into tokens so "sta un" matches "Stati Uniti"
    const toks = q.split(/\s+/);
    return pool.filter((c) => toks.every((t) => c._hay.includes(t)));
  }, [pool, query]);

  const selected = value ? findCountryByIso(value) : null;
  const selectedLocalized = selected ? (getLocalizedCountryName(selected.code, lang) || selected.name_it) : null;

  const handleSelect = (code) => {
    onChange?.(code);
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.("");
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-required={required}
        className={`w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white text-left focus:border-lojack-red focus:outline-none flex items-center justify-between gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${open ? 'border-lojack-red' : ''}`}
        data-testid={testId}
      >
        <span className="flex items-center gap-2 min-w-0 flex-1">
          {selected ? (
            <>
              <span className="text-lg leading-none flex-shrink-0">{selected.flag}</span>
              <span className="truncate">{selectedLocalized}</span>
            </>
          ) : (
            <span className="text-gray-500 truncate">{placeholder || t.placeholder}</span>
          )}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {selected && !required && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 hover:bg-gray-700 rounded transition-colors"
              aria-label="Clear"
              data-testid={`${testId}-clear`}
            >
              <X size={14} className="text-gray-500 hover:text-gray-300" />
            </button>
          )}
          <ChevronDown size={16} className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div
          className="absolute z-[80] mt-1 left-0 right-0 bg-[#141414] border border-gray-700 rounded-xl shadow-2xl overflow-hidden"
          data-testid={`${testId}-popover`}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-700">
            <SearchIcon size={14} className="text-gray-500 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full bg-transparent text-white text-sm placeholder-gray-500 focus:outline-none"
              data-testid={`${testId}-search`}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  setOpen(false);
                  setQuery("");
                } else if (e.key === "Enter" && results.length > 0) {
                  e.preventDefault();
                  handleSelect(results[0].code);
                }
              }}
            />
          </div>

          <div
            className="max-h-64 overflow-y-auto"
            data-testid={`${testId}-list`}
          >
            {results.length === 0 ? (
              <div
                className="px-3 py-4 text-center text-gray-500 text-sm"
                data-testid={`${testId}-empty`}
              >
                {t.empty}
              </div>
            ) : (
              results.map((c) => {
                const isSelected = c.code === value;
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => handleSelect(c.code)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-lojack-red/10 transition-colors ${isSelected ? 'bg-lojack-red/20' : ''}`}
                    data-testid={`${testId}-option-${c.code}`}
                  >
                    <span className="text-lg leading-none flex-shrink-0">{c.flag}</span>
                    <span className="flex-1 text-white text-sm truncate">{c.localized}</span>
                    <span className="text-gray-500 text-xs flex-shrink-0 font-mono">{c.code}</span>
                    {isSelected && <Check size={14} className="text-lojack-red flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

CountryCombobox.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  lang: PropTypes.string,
  excludeCode: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  testId: PropTypes.string,
  className: PropTypes.string,
};

export default CountryCombobox;
